// CombinationTeamTask.js
// Kombinacje bez powtórzeń — wybór k-osobowej drużyny z n osób

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput from "../../../../components/inputs/FractionInput";

// Stabilne liczenie C(n,k)
function nCk(n, k) {
  if (k < 0 || k > n) return 0;
  k = Math.min(k, n - k);
  let num = 1, den = 1;
  for (let i = 1; i <= k; i++) {
    num *= (n - k + i);
    den *= i;
  }
  return Math.floor(num / den);
}

export function generateTask() {
  const n = Math.floor(Math.random() * 7) + 8; // 8..14
  const k = Math.min(Math.random() * 3 + 3 | 0, n); // 3..5
  const answer = nCk(n, k);
  return { type: "CombinationTeamTask", data: { n, k, answer } };
}

export function renderTask({ data }) {
  const { n, k } = data;
  return (
    <div style={{ textAlign: "left" }}>
      <p>
        Z <InlineMath math={`${n}`} /> osób chcemy wybrać{" "}
        <InlineMath math={`${k}`} />-osobową drużynę.
      </p>
      <p>Na ile sposobów można wybrać składy takich drużyn?</p>
    </div>
  );
}

export function renderInput(_task, userAnswer, onChange, disabled) {
  return (
    <FractionInput
      value={userAnswer}
      onChange={onChange}
      disabled={disabled}
      label=""
      placeholder="Wpisz liczbę sposobów"
    />
  );
}

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object") {
    return { correct: false, message: "Podaj liczbę." };
  }
  const given = parseInt(userAnswer.raw, 10);
  if (Number.isNaN(given)) {
    return { correct: false, message: "Podaj liczbę całkowitą." };
  }
  const correct = given === data.answer;
  userAnswer.correctAnswer = data.answer;
  return { correct, message: correct ? "Dobrze!" : "Spróbuj ponownie." };
}

export function renderAnswer({ data }) {
  return <InlineMath math={`${data.answer}`} />;
}

export function renderUserAnswer(userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object" || !userAnswer.raw) {
    return <i>(brak odpowiedzi)</i>;
  }
  return <InlineMath math={`${userAnswer.raw}`} />;
}

export function renderExplanation({ data }) {
  const { n, k, answer } = data;

  // Skracamy z większą silnią (L), zostaje krótszy iloczyn i mniej rachunków.
  const L = Math.max(k, n - k);       // większa z k i (n-k)
  const S = Math.min(k, n - k);       // mniejsza z k i (n-k)

  // Po skróceniu n! / L! = n·(n-1)·...·(L+1) (S czynników)
  const topFactorsArr = Array.from({ length: n - L }, (_, i) => n - i); // n, n-1, ..., L+1
  const topLatex = topFactorsArr.join("\\cdot");
  const botFactorsArr = Array.from({ length: S }, (_, i) => S - i);     // S, S-1, ..., 1
  const botLatex = botFactorsArr.join("\\cdot");

  const topVal = topFactorsArr.reduce((a, b) => a * b, 1);
  const botVal = botFactorsArr.reduce((a, b) => a * b, 1);

  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <p>
        <strong>Krok 1. Dedukcja i wzór.</strong> Wybieramy podzbiory{" "}
        <InlineMath math={`${k}`} />-elementowe ze zbioru{" "}
        <InlineMath math={`${n}`} /> — to są <b>kombinacje bez powtórzeń</b>:
      </p>
      <BlockMath math={'C(n,k)=\\binom{n}{k}=\\dfrac{n!}{k!(n-k)!}'} />

      <p><strong>Krok 2. Podstawienie, skracanie większą silnią i obliczenie.</strong></p>
      {/* Podstawienie + pokazanie wyniku odejmowania (n-k) */}
      <BlockMath math={`\\binom{${n}}{${k}}=\\dfrac{${n}!}{${k}!\\,( ${n}-${k})!} \\,=\\, \\dfrac{${n}!}{${k}!\\,${n-k}!}`} />
      {/* Skracamy z większą silnią L! i przechodzimy od razu do krótkiego iloczynu / S! */}
      <BlockMath math={`=\\dfrac{${topLatex}}{${S}!}`} />
      {/* Rozwijamy S! i liczymy */}
      <BlockMath math={`=\\dfrac{${topLatex}}{${botLatex}}`} />
      <BlockMath math={`=\\dfrac{${topVal}}{${botVal}}=${answer}`} />
    </div>
  );
}
